import {
  Alert,
  AlertOverviewEntry,
  AlertPriority,
  AlertPriorityOrder,
  AlertStatus,
  AlertV2Entry,
  WatcherrApiNotFoundError,
  WebSocketService,
} from '@watcherr/resources';
import { State } from '../types';
import SoundService from '~/services/SoundService';
import uniq from 'lodash/uniq';
import { AlertReceivedMethodSource, SimpleAlert } from './Alerts.types';
import { getAlertMethod, transformIntoSimpleAlert } from './Alert.utils';
import { action, computed, makeAutoObservable, runInAction } from 'mobx';
import AlertStore from './Alert';
import PersistentStoreService from '~services/PersistentStoreService';
import { backoff } from '~utils/promises';
import NotificationService from '~services/NotificationService';
import { endOfDay, startOfDay, subDays } from 'date-fns';

const RECEIVED_ALERT_UUIDS_KEY = 'receivedAlertUuids';
const RECEIVED_ALERT_UUIDS_LIMIT = 100;

/*
Receives alerts from multiple sources (e.g. api calls, websockets, notifications)
Keeps a record of which alerts are still active
Controls the SoundService
*/
class AlertsStore {
  private rootStore: State;

  // Simple alerts from API,WS,PUSH
  private activeAlerts: SimpleAlert[] = [];
  private inactiveAlerts: SimpleAlert[] = [];
  private rejectedAlerts: SimpleAlert[] = [];

  // Full detailed alerts from API,WS
  public alertStoreMap = new Map<string, AlertStore>();

  private isFetchingApi = false;
  private isFetchingLatestApi = false;
  private lastUpdate: number = 0;
  private didFailRefresh = false;

  private interval?: any;

  private socket: WebSocketService<AlertV2Entry[]> | null = null;
  public isSocketConnected: boolean = false;
  public isSocketConnecting: boolean = true;

  static readonly INTERNAL_SYSTEM_RESOLVE_REASON = 'Resolving duplicate alert message';

  constructor(rootStore: State) {
    this.rootStore = rootStore;
    makeAutoObservable(this, {
      updateKnownAlerts: action.bound,
      rejectAlerts: action.bound,
      fetchAlerts: action.bound,
      isDisconnected: computed,
    });
  }

  private get knownAlerts() {
    return this.activeAlerts.concat(this.inactiveAlerts);
  }

  private isInactive(alert: SimpleAlert) {
    return this.inactiveAlerts.some((a) => alert.uuid === a.uuid);
  }

  private isActive(alert: SimpleAlert) {
    return this.activeAlerts.some((a) => alert.uuid === a.uuid);
  }

  private isRejected(alert: SimpleAlert) {
    return this.rejectedAlerts.some((a) => alert.uuid === a.uuid);
  }

  updateKnownAlerts = (alerts: AlertOverviewEntry[] | AlertV2Entry[]): boolean => {
    if (!this.rootStore.auth.isLoggedIn) return false;

    const simpleAlerts = transformIntoSimpleAlert(alerts);
    let isModified = false;
    for (const alert of simpleAlerts) {
      if (alert.status === AlertStatus.Open) {
        // Check if due to race condition this has already been closed
        if (this.isInactive(alert)) continue;
        if (this.isActive(alert)) continue;
        isModified = true;
        this.activeAlerts.push(alert);
      } else {
        if (this.isInactive(alert)) continue;
        isModified = true;
        this.activeAlerts = this.activeAlerts.filter((a) => a.uuid !== alert.uuid);
        this.inactiveAlerts.push(alert);
      }

      this.alertStoreMap.get(alert.uuid)?.setStatus(alert.status);
    }

    if (isModified) {
      this.informExternals();
    }

    //this.processAcknowledgements(simpleAlerts, source).catch(console.warn);
    return isModified;
  };

  private removeAlertFromState(uuid: string) {
    this.activeAlerts = this.activeAlerts.filter((x) => x.uuid !== uuid);
    this.inactiveAlerts = this.inactiveAlerts.filter((x) => x.uuid !== uuid);
    this.rejectedAlerts = this.rejectedAlerts.filter((x) => x.uuid !== uuid);
    this.alertStoreMap.delete(uuid);
    this.informExternals();
  }

  // Let external services know that the alerts state was updated
  private informExternals() {
    this.updateSounds();
    NotificationService.updateAlertCounters(this.activeAlerts);
  }

  // Keep a record of the last 100 received alerts
  // to prevent duplicate acknowledgements
  private processAcknowledgements = async (simpleAlerts: SimpleAlert[], source: AlertReceivedMethodSource) => {
    const uuids = (await PersistentStoreService.get<string[]>(RECEIVED_ALERT_UUIDS_KEY)) ?? [];
    const diff = simpleAlerts.filter((x) => !uuids.includes(x.uuid));
    if (diff.length) {
      const updatedUuids = uuids.concat(diff.map((x) => x.uuid)).slice(-RECEIVED_ALERT_UUIDS_LIMIT);
      await PersistentStoreService.set(RECEIVED_ALERT_UUIDS_KEY, updatedUuids);

      await Promise.all(diff.map((alert) => this.acknowledgeAlert(alert, source)));
    }
  };

  private acknowledgeAlert = async (alert: SimpleAlert, source: AlertReceivedMethodSource) => {
    const methodKey = getAlertMethod(source);
    try {
      await backoff(
        (signal) =>
          Alert.createAlertAcknowledgement(
            {
              alertUuid: alert.uuid,
              method: methodKey,
            },
            signal
          ),
        { maxRetries: 15 }
      );
    } catch (err) {
      console.error('Alert.createAlertAcknowledgement', err);
    }
  };

  rejectAlerts(alerts: AlertStore[]) {
    const simpleAlerts = transformIntoSimpleAlert(alerts);
    let isModified = false;

    for (const alert of simpleAlerts) {
      if (this.isRejected(alert)) return;
      if (!this.isActive(alert)) return;
      console.info('AlertsStore', 'reject', alert.uuid);
      this.rejectedAlerts.push(alert);
      isModified = true;
    }

    if (isModified) {
      this.informExternals();
    }
  }

  private updateSounds() {
    try {
      const activeTypes = this.activeAlerts
        .filter((a) => !this.rejectedAlerts.some((b) => a.uuid === b.uuid))
        .map((a) => a.priority);
      SoundService.updateActiveSoundTypes(uniq(activeTypes));
    } catch (err) {
      console.error(err);
    }
  }

  get alertsSorted() {
    return this.knownAlerts
      .map((sa) => this.alertStoreMap.get(sa.uuid))
      .filter((a): a is AlertStore => !!a)
      .sort((a, b) => b.created.getTime() - a.created.getTime());
  }

  get openAlerts() {
    return this.alertsSorted.filter((a) => a.isOpen);
  }

  get cancelledAlerts() {
    return this.alertsSorted.filter((a) => a.isCancelled);
  }

  get acceptedAlerts() {
    return this.alertsSorted.filter((a) => a.isAcceptedOwnExcluded);
  }

  get isDisconnected() {
    return !this.isSocketConnected && Date.now() - this.lastUpdate > 40e3;
  }

  // The app received a new active alert (via PUSH) but hasn't been able to fetch it from the API yet
  get isInLimbo() {
    return this.activeAlerts.some((x) => !this.alertStoreMap.has(x.uuid));
  }

  get isLoading() {
    return this.isFetchingApi || this.isSocketConnecting || this.isInLimbo;
  }

  get groupedAlerts(): {
    [key in AlertPriority]: { items: AlertStore[]; open: number };
  } {
    const grouped = AlertPriorityOrder.reduce<any>((result, prio) => {
      const alerts = this.alertsSorted.filter((alert) => alert.priority === prio);
      result[prio] = {
        items: alerts,
        open: alerts.filter((a) => a.isOpen).length,
      };
      return result;
    }, {});
    return grouped;
  }

  fetchAlerts = async (maxRetries = 3) => {
    if (this.isFetchingApi) {
      return;
    }

    this.startListening();

    this.isFetchingApi = true;

    try {
      const alerts = await backoff(() => Alert.getAlertsV2({ limit: 25 }), { maxRetries });

      runInAction(() => {
        if (this.didFailRefresh) {
          this.didFailRefresh = false;
        }

        this.isFetchingApi = false;
        this.updateAlertOverviewStoreMap(alerts);
        this.updateKnownAlerts(alerts);

        // There is a chance that old OPEN alerts aren't in the API response
        // Check if there are any missing and fetch them
        const missingActiveAlerts = this.activeAlerts.filter((x) => !alerts.some((y) => x.uuid === y.uuid));
        if (missingActiveAlerts.length) {
          console.info('Alerts', 'missing active alerts', missingActiveAlerts.length);
        }
        missingActiveAlerts.map((a) => this.fetchAlert(a.uuid));
      });
    } catch (error) {
      runInAction(() => {
        this.isFetchingApi = false;
        this.didFailRefresh = true;
      });
      NotificationService.disconnected();
      console.error('alerts api error', error);
    }
  };

  fetchLatestAlerts = async ({ numberOfDaysAgo = 1, maxRetries = 3, forceFetch = false }) => {
    if (!forceFetch && (this.isFetchingLatestApi || this.lastUpdate > Date.now() - 15e3)) {
      return;
    }

    this.startListening();

    this.isFetchingLatestApi = true;

    const end = endOfDay(new Date());
    const start = startOfDay(subDays(end, numberOfDaysAgo));

    try {
      const alertEvents = await backoff(
        (signal) => Alert.getAlertsHistoryV2({ start_date: start.toISOString(), end_date: end.toISOString() }, signal),
        { maxRetries }
      );

      const alerts: AlertOverviewEntry[] = alertEvents.reduce((acc: AlertOverviewEntry[], cur) => {
        const alert_id = cur.uuid;

        if (cur.resolvedBy && cur.resolvedReason === AlertsStore.INTERNAL_SYSTEM_RESOLVE_REASON) {
          return acc;
        }

        if (!acc.some((a) => a.uuid === alert_id)) {
          acc.push(cur);
        }

        return acc;
      }, []);

      runInAction(() => {
        if (this.didFailRefresh) {
          this.didFailRefresh = false;
        }

        this.isFetchingLatestApi = false;
        this.updateAlertOverviewStoreMap(alerts);
        this.updateKnownAlerts(alerts);

        // There is a chance that old OPEN alerts aren't in the API response
        // Check if there are any missing and fetch them
        const missingActiveAlerts = this.activeAlerts.filter((x) => !alerts.some((y) => x.uuid === y.uuid));
        if (missingActiveAlerts.length) {
          console.info('Alerts', 'missing active alerts', missingActiveAlerts.length);
        }
        missingActiveAlerts.map((a) => this.fetchAlert(a.uuid));
      });
    } catch (error) {
      runInAction(() => {
        this.isFetchingLatestApi = false;
        this.didFailRefresh = true;
      });
      NotificationService.disconnected();
      console.error('alerts api error', error);
    }
  };

  fetchAlert = (uuid: string) => {
    backoff((signal) => Alert.getAlertV2(uuid, signal))
      .then((alert) => {
        runInAction(() => {
          this.updateAlertDetailStoreMap([alert]);
          this.updateKnownAlerts([alert]);
        });
      })
      .catch((e) => {
        if (e instanceof WatcherrApiNotFoundError) {
          // GHOST alert
          // It was created at one point, but now it's gone
          console.warn(`Removing ghost alert`, uuid);
          runInAction(() => {
            this.removeAlertFromState(uuid);
          });
        } else {
          console.info(`alert error api`, e);
        }
      });
  };

  private updateAlertDetailStoreMap(alerts: AlertV2Entry[]) {
    for (const alert of alerts) {
      const existingAlertStore = this.alertStoreMap.get(alert.uuid);
      if (existingAlertStore) {
        existingAlertStore.setDetailData(alert);
      } else {
        this.alertStoreMap.set(alert.uuid, new AlertStore(this.rootStore, alert));
      }
    }
  }

  private updateAlertOverviewStoreMap(alerts: AlertOverviewEntry[]) {
    for (const alert of alerts) {
      const existingAlertStore = this.alertStoreMap.get(alert.uuid);
      if (existingAlertStore) {
        existingAlertStore.setOverviewData(alert);
      } else {
        this.alertStoreMap.set(alert.uuid, new AlertStore(this.rootStore, undefined, alert));
      }
    }
    this.lastUpdate = Date.now();
  }

  private startListening() {
    if (!this.socket && this.rootStore.auth.isLoggedIn) {
      runInAction(() => {
        this.isSocketConnected = false;
        this.isSocketConnecting = true;
      });
      console.info(`Start listening to alerts`);

      this.socket = Alert.listenOnNewAlerts(this.handleWsNewAlerts);

      this.socket.onClose = this.handleWsClose;

      this.socket.onOpen = this.handleWsOpen;

      this.socket.open();
    }
    if (!this.interval) {
      this.interval = setInterval(() => this.fetchLatestAlerts({ numberOfDaysAgo: 1 }), 3 * 60e3);
    }
  }

  private stopListening = () => {
    console.info(`close listening to alerts`);
    if (this.socket) {
      this.socket.forceDisconnect();
      this.socket = null;
    }
  };

  private handleWsNewAlerts = (data: AlertV2Entry[]) => {
    runInAction(() => {
      this.isSocketConnected = true;
    });

    this.updateAlertDetailStoreMap(data);
    this.updateKnownAlerts(data);
  };

  private handleWsOpen = () => {
    console.info('websocket opened');
    runInAction(() => {
      this.isSocketConnected = true;
      this.isSocketConnecting = false;
    });
  };

  private handleWsClose = () => {
    console.info('websocket closed in foreground');

    runInAction(() => {
      this.isSocketConnected = false;
      this.isSocketConnecting = false;
    });
  };

  reset = () => {
    this.activeAlerts = [];
    this.inactiveAlerts = [];
    this.rejectedAlerts = [];

    this.stopListening();
    this.alertStoreMap.clear();
    this.isSocketConnected = false;
    this.isSocketConnecting = true;
    this.lastUpdate = 0;
    this.isFetchingApi = false;
    this.isFetchingLatestApi = false;
  };
}

export default AlertsStore;
